import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { siteInfo, aboutContent } from '@/mocks/articles';
import { generateOrganizationSchema, generateWebPageSchema, generateFAQPageSchema, generateBreadcrumbSchema } from '@/components/feature/StructuredData';

export default function AboutPage() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const pageTitle = '关于我们';
  const canonicalUrl = `https://${siteInfo.domain}/about`;
  const pageDesc = '了解黑料91品牌背景、平台口碑与联系方式。国内领先的娱乐爆料聚合平台，致力于为用户提供最真实的黑料资讯。';

  const organizationLD = generateOrganizationSchema();
  const webPageLD = generateWebPageSchema(pageTitle, pageDesc, canonicalUrl, [
    { name: '首页', url: `https://${siteInfo.domain}` },
    { name: '关于我们', url: canonicalUrl },
  ]);
  const faqLD = generateFAQPageSchema(aboutContent.faq);

  return (
    <>
      <SEOHead
        title={`${pageTitle} - ${siteInfo.name}`}
        description={pageDesc}
        keywords="黑料91,关于黑料91,黑料91联系方式,黑料91品牌介绍"
        canonicalUrl={canonicalUrl}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationLD) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageLD) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLD) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <nav className="w-full max-w-5xl mx-auto px-4 md:px-6 py-3 text-sm text-foreground-500" aria-label="面包屑导航">
            <ol className="flex items-center gap-1.5 flex-wrap">
              <li><Link to="/" className="hover:text-primary-600 transition-colors">首页</Link></li>
              <li>/</li>
              <li className="text-foreground-950 font-medium">关于我们</li>
            </ol>
          </nav>

          <section className="w-full max-w-5xl mx-auto px-4 md:px-6 py-8">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-2">
              {aboutContent.brandIntro.title}
            </h1>
            <p className="text-foreground-500 mb-8">
              国内领先的娱乐资讯与爆料聚合平台
            </p>

            <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8 mb-10">
              {aboutContent.brandIntro.paragraphs.map((p, i) => (
                <p key={i} className="text-foreground-700 leading-relaxed mb-4 last:mb-0">
                  {p}
                </p>
              ))}
            </div>

            <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-6">
              {aboutContent.reputation.title}
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              {aboutContent.reputation.items.map((item) => (
                <div
                  key={item.label}
                  className="bg-background-50 rounded-lg border border-background-200/70 p-5 text-center"
                >
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">
                    {item.value}
                  </div>
                  <div className="text-sm text-foreground-500">{item.label}</div>
                </div>
              ))}
            </div>

            <h3 className="text-lg font-heading font-bold text-foreground-950 mb-4">用户评价</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
              {aboutContent.reputation.testimonials.map((t, i) => (
                <div
                  key={i}
                  className="bg-background-100/70 rounded-lg p-5 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <p className="text-xs text-foreground-500 font-medium">{t.author}</p>
                </div>
              ))}
            </div>

            <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-6">
              {aboutContent.contact.title}
            </h2>

            <div className="bg-background-100/70 rounded-lg border border-background-200/70 p-6 md:p-8 mb-10">
              <p className="text-foreground-600 mb-6">{aboutContent.contact.description}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-primary-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">公司地址</p>
                    <p className="text-sm text-foreground-500">{aboutContent.contact.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">电子邮箱</p>
                    <p className="text-sm text-foreground-500">{aboutContent.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-primary-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">联系电话</p>
                    <p className="text-sm text-foreground-500">{aboutContent.contact.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-primary-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">工作时间</p>
                    <p className="text-sm text-foreground-500">{aboutContent.contact.workHours}</p>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-6">常见问题</h2>
            <div className="space-y-4 mb-10">
              {aboutContent.faq.map((faq, i) => (
                <div
                  key={i}
                  className="bg-background-50 rounded-lg border border-background-200/70 p-5"
                >
                  <h4 className="text-base font-semibold text-foreground-950 mb-2">
                    <a id={`faq-${i + 1}`} className="no-underline">
                      {faq.question}
                    </a>
                  </h4>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>

            <div className="text-center py-6">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                返回首页
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}